// Country signal helpers for PIN/ZIP handling.
//
// Currency (from the CSOC subscription plan) is the PRIMARY signal for deciding
// which location input to show. Mobile number / country_code are the FALLBACK.
//
// This list mirrors NO_POSTAL_CODE_DIAL_CODES in the chess and chatbot backends —
// keep them in sync.
const NO_POSTAL_CODE_DIAL_CODES = ['971', '974', '965', '973', '968', '852', '853', '353'];

const NO_POSTAL_CODE_COUNTRIES = [
  'united arab emirates',
  'uae',
  'qatar',
  'kuwait',
  'bahrain',
  'oman',
  'hong kong',
  'macau',
  'macao',
  'ireland',
];

type CountrySignal = {
  mobile_number?: string | null;
  country_code?: string | null;
  country?: string | null;
  currency?: string | null;
};

/**
 * Location collection mode — mirrors the backend get_location_collection_config modes.
 * indian_pincode          : Indian mobile + INR → strict 6-digit Indian PIN
 * non_indian_inr_pincode  : Non-Indian mobile + INR → strict 6-digit Indian PIN (govt compliance)
 * foreign_zip             : Non-INR + postal-code country → alphanumeric ZIP
 * city                    : Non-INR + no-postal-code country → free-text city
 */
export type LocationMode =
  | 'indian_pincode'
  | 'non_indian_inr_pincode'
  | 'foreign_zip'
  | 'city';

export type LocationConfig = {
  mode: LocationMode;
  title: string;
  description: string;
  placeholder: string;
  input_field: 'pincode' | 'city';
};

const onlyDigits = (value?: string | null) => (value || '').replace(/\D/g, '');

export function isNoPostalCodeCountry(user?: CountrySignal | null): boolean {
  if (!user) return false;

  const country = (user.country || '').trim().toLowerCase();
  if (country && NO_POSTAL_CODE_COUNTRIES.includes(country)) return true;

  const dial = onlyDigits(user.country_code);
  if (dial && NO_POSTAL_CODE_DIAL_CODES.includes(dial)) return true;

  const mobile = onlyDigits(user.mobile_number);
  if (mobile && NO_POSTAL_CODE_DIAL_CODES.some(code => mobile.startsWith(code))) return true;

  return false;
}

export function isPostalCodeRequired(user?: CountrySignal | null): boolean {
  return !isNoPostalCodeCountry(user);
}

/**
 * Validate a pincode/zip value given the resolved LocationMode.
 * - indian_pincode / non_indian_inr_pincode → strict ^[1-9][0-9]{5}$
 * - foreign_zip → alphanumeric, 2-12 chars
 * - city → any non-empty string is valid
 * - Empty is always valid (required-ness is a separate concern).
 */
export function isValidPincode(
  pincode?: string | null,
  user?: CountrySignal | null,
  mode?: LocationMode | null,
): boolean {
  const code = (pincode || '').trim();
  if (!code) return true;

  if (mode === 'indian_pincode' || mode === 'non_indian_inr_pincode') {
    return /^[1-9][0-9]{5}$/.test(code);
  }
  if (mode === 'city') return true;
  if (mode === 'foreign_zip') {
    return /^[A-Za-z0-9][A-Za-z0-9 -]{1,11}$/.test(code);
  }

  // Fallback: derive from currency + country signals (legacy call sites).
  if (user?.currency && user.currency.toUpperCase() === 'INR') {
    return /^[1-9][0-9]{5}$/.test(code);
  }
  if (isNoPostalCodeCountry(user)) return true;
  const mobile = onlyDigits(user?.mobile_number);
  if (mobile.startsWith('91')) return /^[1-9][0-9]{5}$/.test(code);
  if (!mobile && /^\d{6}$/.test(code)) return /^[1-9][0-9]{5}$/.test(code);
  return /^[A-Za-z0-9][A-Za-z0-9 -]{1,11}$/.test(code);
}

/**
 * Derive a LocationConfig client-side as a fallback when the backend hasn't
 * returned one yet. Mirrors get_location_collection_config in chess/utils/utils.py.
 *
 * Currency is only used when explicitly provided AND trusted (i.e. from the
 * actual payment order, not the subscription plan). When currency is absent,
 * mobile number is the sole signal — subscription plan currency is always INR
 * and cannot distinguish Indian from non-Indian payers.
 */
export function deriveLocationConfig(user?: CountrySignal | null): LocationConfig {
  const mobile = onlyDigits(user?.mobile_number);
  const isIndianMobile = mobile.startsWith('91') && mobile.length === 12;
  const isNoPostal = isNoPostalCodeCountry(user);
  const currency = (user?.currency || '').toUpperCase();
  const hasCurrency = currency.length > 0;

  // When currency is available (from backend payment order), use all 4 cases.
  if (hasCurrency) {
    const isINR = currency === 'INR';
    if (isINR && isIndianMobile) {
      return {
        mode: 'indian_pincode',
        title: 'Enter Your PIN Code',
        description:
          'Please provide your PIN code for your invoice. Since you have made the payment in INR, we need 6 digit valid Indian pincode as per government rules and regulations.',
        placeholder: '6-digit PIN Code',
        input_field: 'pincode',
      };
    }
    if (isINR) {
      return {
        mode: 'non_indian_inr_pincode',
        title: 'Enter Your PIN Code',
        description:
          'Please provide your PIN code for your invoice. Since you have made the payment in INR, we need 6 digit valid Indian pincode as per government rules and regulations.',
        placeholder: '6-digit PIN Code',
        input_field: 'pincode',
      };
    }
    if (isNoPostal) {
      return {
        mode: 'city',
        title: 'Enter Your City',
        description: 'Please provide your city for your invoice as per rules and regulation compliance.',
        placeholder: 'City',
        input_field: 'city',
      };
    }
    return {
      mode: 'foreign_zip',
      title: 'Enter Your ZIP / Postal Code',
      description: 'Please provide your ZIP or postal code for your invoice as per rules and regulation compliance.',
      placeholder: 'ZIP / Postal Code',
      input_field: 'pincode',
    };
  }

  // No currency — use mobile number only.
  if (isNoPostal) {
    return {
      mode: 'city',
      title: 'Enter Your City',
      description: 'Please provide your city for your invoice as per rules and regulation compliance.',
      placeholder: 'City',
      input_field: 'city',
    };
  }
  return {
    mode: isIndianMobile ? 'indian_pincode' : 'foreign_zip',
    title: isIndianMobile ? 'Enter Your PIN Code' : 'Enter Your ZIP / Postal Code',
    description: isIndianMobile
      ? 'Please provide your 6-digit PIN code for your invoice as per government rules and regulations.'
      : 'Please provide your ZIP or postal code for your invoice as per rules and regulation compliance.',
    placeholder: isIndianMobile ? '6-digit PIN Code' : 'ZIP / Postal Code',
    input_field: 'pincode',
  };
}

export function isCityRequired(user?: CountrySignal | null): boolean {
  return isNoPostalCodeCountry(user);
}
